import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import {
  OrderNumber,
  PublicOrderDataSourceService,
  PublicOrderShippingAddressForm,
} from '@web/shared/module/shop';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, finalize, Observable, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorService } from '@web/common';
import { PublicOrderShippingAddressFormComponent } from '../public-order-shipping-address-form/public-order-shipping-address-form.component';
import { Guid } from '@web/utils';

@Component({
  selector: 'app-public-order-shipping-address-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertsComponent,
    SpinnerFilledComponent,
    PublicOrderShippingAddressFormComponent,
  ],
  templateUrl: './public-order-shipping-address-edit.component.html',
  styleUrl: './public-order-shipping-address-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class PublicOrderShippingAddressEditComponent {
  public readonly orderNumber = input.required<OrderNumber>();
  public readonly shipmentId = input.required<Guid>();
  public readonly submitEvent = output<void>();
  public readonly cancelEvent = output<void>();

  protected readonly pending = signal(false);
  protected readonly orderNumber$ = this.orderNumberSource();
  protected readonly shipmentId$ = this.shipmentIdSource();
  protected readonly data$ = this.dataSource();

  /* prettier-ignore */
  private readonly orderDataSource = inject(PublicOrderDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public handleSubmitClickEvent(data: PublicOrderShippingAddressForm): void {
    this.markAsPending();

    this.orderDataSource
      .updateShippingAddressByFormData({
        orderNumber: this.orderNumber(),
        shipmentId: this.shipmentId(),
        data,
      })
      .pipe(finalize(() => this.markAsResolved()))
      .subscribe({
        next: () => this.submitEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  public handleCancelClickEvent(): void {
    this.cancelEvent.emit();
  }

  private markAsPending(): void {
    this.pending.set(true);
  }

  private markAsResolved(): void {
    this.pending.set(false);
  }

  private orderNumberSource(): Observable<OrderNumber> {
    return toObservable(this.orderNumber);
  }

  private shipmentIdSource(): Observable<Guid> {
    return toObservable(this.shipmentId);
  }

  private dataSource(): Observable<PublicOrderShippingAddressForm> {
    return combineLatest({
      orderNumber: this.orderNumber$,
      shipmentId: this.shipmentId$,
    }).pipe(
      switchMap(({ shipmentId, orderNumber }) =>
        this.orderDataSource.fetchShippingAddressFormByNumber({
          orderNumber,
          shipmentId,
        }),
      ),
    );
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }
}
